import { useState, useEffect } from 'react';
import cogoToast from 'cogo-toast';
import { getSmartImage } from '../helpers/helpers';

export const ImageUploaderInput = ({ url: passedUrl, onUpload }) => {
  const [inputUrl, setInputUrl] = useState(passedUrl ?? '');
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const hasInput = !!inputUrl.trim();

  useEffect(() => {
    setInputUrl(passedUrl ?? '');
  }, [passedUrl]);

  const handleDownloadImage = async e => {
    e.preventDefault();
    e.stopPropagation();

    if (!hasInput) {
      cogoToast.error('Please enter a URL');
      return;
    }

    setIsDownloading(true);

    try {
      const response = await fetch(inputUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const contentType = response.headers.get('content-type');

      if (!contentType || !contentType.startsWith('image/')) {
        cogoToast.error('URL does not point to an image');
        return;
      }

      const signingUrl = new URL(process.env.REACT_APP_API_URL);
      signingUrl.pathname = '/s3/sign';
      signingUrl.searchParams.set('objectName', `image-${Date.now()}`);
      signingUrl.searchParams.set('contentType', contentType);
      const signResponse = await fetch(signingUrl);

      if (!signResponse.ok) {
        throw new Error(`Failed to get S3 signature: ${signResponse.status} ${signResponse.statusText}`);
      }

      const { signedUrl } = await signResponse.json();

      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': contentType
        }
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      const s3Url = getSmartImage(signedUrl.split('?')[0]);
      setInputUrl(s3Url);
      setUploadedUrl(s3Url);

      onUpload?.(s3Url);
    } catch (error) {
      if (error.response?.status === 404) {
        cogoToast.error('Image not found. Please enter a valid URL.');
      } else if (error.message.includes('Network Error')) {
        cogoToast.error('Network error. Please download the image and upload it manually.');
      } else {
        cogoToast.error('Failed to upload image. Please download the image and upload it manually.');
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <form onSubmit={handleDownloadImage} onClick={e => e.stopPropagation()}>
      <input name='url' type='url' placeholder='Or enter an image URL' value={inputUrl} onChange={e => setInputUrl(e.target.value)} />

      {inputUrl !== uploadedUrl && hasInput && (
        <div className='buttons'>
          <button type='submit' disabled={isDownloading} className='save-button'>
            {isDownloading ? 'Downloading...' : 'Download'}
          </button>
        </div>
      )}
    </form>
  );
};
